import { Translator } from 'Translator';
import Card from 'components/Card';
import ExperienceLinkContainer from 'components/ExperienceLinkContainer';
import LazyImage from 'components/LazyImage';
import useIsMobile from 'hooks/useIsMobile';
import SortedHostProvider from 'modules/experience/providers/SortedHostProvider';
import React, { useCallback, useMemo, useState } from 'react';
import VisibilitySensor from 'react-visibility-sensor';
import { analyticsTrack, useTrekker } from 'utils/analytics';

import TopSearchCardDetails from './TopSearchCardDetails';
import TopSearchCardHostAvatar from './TopSearchCardHostAvatar';

const CSS = require('./TopSearchCard.less');

const defaultOffset = {
  top: -200,
  left: -32,
  right: -32,
  bottom: -200,
};
const createOffset = (width) => ({ ...defaultOffset, left: -width });

const IMAGE_OPTIONS = {
  xs: { width: 360, height: 202 },
  lg: { width: 533, height: 300 },
};

interface TopSearchCardProps {
  position: number;
  analyticsListName?: string;
  experience: any;
  className?: string;
  nrOfAdults: number;
  nrOfChildren: number;
  useAdvertisedPrice?: boolean;
}

function TopSearchCard({
  experience,
  position = 0,
  nrOfAdults,
  nrOfChildren,
  useAdvertisedPrice,
  analyticsListName,
}: TopSearchCardProps) {
  const [visible, setVisible] = useState(false);
  const trekker = useTrekker();
  const isMobile = useIsMobile();
  const context = useMemo(
    () => ({
      component: ['top-search-card'],
      experienceCard: experience,
      analyticsListName,
    }),
    [experience, analyticsListName],
  );
  const handleWaypointEnter = useCallback(
    (isVisible) => {
      if (isVisible) {
        setVisible(true);
        trekker.event('view', context);
      }
    },
    [context, trekker],
  );

  const imageOptions = isMobile ? IMAGE_OPTIONS.xs : IMAGE_OPTIONS.lg;
  const offset = createOffset(imageOptions.width);
  const altText = `${experience.title} in ${
    experience.city
  } - ${experience.mainPhoto.split('/').slice(-1)}`;

  const analyticsCallback = useCallback(() => {
    analyticsTrack(
      {
        subject: 'clickedExperience Top 5 Slider',
        type: 'click',
        valueString: experience.title,
      },
      {
        category: 'clickedExperience Top 5 Slider',
        action: 'click',
        label: experience.title,
      },
    );
    trekker.event('click', context);
  }, [context, experience.title, trekker]);

  return (
    <SortedHostProvider experienceId={experience.id} hosts={experience.hosts}>
      {({ sortedHosts }) => (
        <Card onClick={analyticsCallback}>
          {!visible && (
            <VisibilitySensor
              delayedCall
              intervalDelay={300}
              onChange={handleWaypointEnter}
              offset={offset}
            />
          )}
          <div className={CSS.TopSearchCard}>
            <div className={CSS.Details}>
              <h3 className={CSS.Title}>
                <ExperienceLinkContainer
                  experience={experience}
                  onClick={analyticsCallback}
                >
                  {`${position} | ${experience.title}`}
                </ExperienceLinkContainer>
              </h3>
              <div className={CSS.Footer}>
                {visible && (
                  <>
                    <TopSearchCardHostAvatar
                      experience={experience}
                      hosts={sortedHosts}
                    />
                    <TopSearchCardDetails
                      hosts={sortedHosts}
                      experience={experience}
                      nrOfAdults={nrOfAdults}
                      nrOfChildren={nrOfChildren}
                      isMobile={isMobile}
                      useAdvertisedPrice={useAdvertisedPrice}
                    />
                    <span className={CSS.ExploreButton}>
                      {Translator.translate(
                        'UserEnvironment.PopularExperiences.explore',
                        {},
                        true,
                        'Explore',
                      )}
                    </span>
                  </>
                )}
              </div>
            </div>
            <LazyImage
              animate
              alt={altText}
              className={CSS.BackgroundImage}
              containerClass={CSS.ImageContainer}
              src={experience.mainPhoto}
              width={imageOptions.width}
              height={imageOptions.height}
              options={imageOptions}
              forceInView
            />
          </div>
        </Card>
      )}
    </SortedHostProvider>
  );
}

export default TopSearchCard;
